import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { postData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";
import { useGuidance } from "./T2TGuidanceProvider";

// ---- Tutorial step definitions ----
const TUTORIAL_STEPS = [
  {
    id: "T1", step: 1,
    title: "Welcome to the Tutorial",
    instruction: "This is the Workflow Builder. You\u2019ll use it to design the steps that any request goes through before it is approved. Let\u2019s build a simple Expense Approval workflow together.",
    target: null, // entire canvas
    action: null, // no action needed — just click Next
    nextLabel: "Let\u2019s add the first step \u2192",
  },
  {
    id: "T2", step: 2,
    title: "Add the First Stage",
    instruction: "Every workflow starts with someone submitting a request. Drag an Approval stage from the palette on the left and drop it on the canvas.",
    target: ".t2t-pal-item", // palette items
    action: "stage_added",
    successMsg: "Perfect! This is your first stage \u2014 the step where an employee fills in their expense claim.",
    nextLabel: "Now name it and move on \u2192",
  },
  {
    id: "T3", step: 3,
    title: "Name the Stage",
    instruction: "Double-click the stage to open its properties and rename it to \u201cExpense Submission\u201d.",
    target: ".t2t-snode-head",
    action: "stage_props_opened",
    successMsg: "Great! You can set the stage name in the properties panel on the right.",
    nextLabel: "Now add the manager approval step \u2192",
  },
  {
    id: "T4", step: 4,
    title: "Add Manager Approval",
    instruction: "Add a second stage \u2014 the manager who will approve or reject the claim. Drag another Approval stage from the palette.",
    target: ".t2t-pal-item",
    action: "stage_added",
    successMsg: "Good! This is where the employee\u2019s manager reviews the expense.",
    nextLabel: "Connect them with an arrow \u2192",
  },
  {
    id: "T5", step: 5,
    title: "Draw the First Arrow",
    instruction: "To connect two stages, click the small circle (\u25B6) on the right edge of the first stage, then click the circle on the left edge of the second stage.",
    target: ".t2t-port-out",
    action: "transition_drawn",
    successMsg: "You just created a transition! This arrow means: when someone submits, it moves to the manager for review.",
    nextLabel: "Now add Finance and connect the whole flow \u2192",
  },
  {
    id: "T6", step: 6,
    title: "Add Finance + End Stage",
    instruction: "Add one more stage: Finance Review. Then drag an End stage from the palette. Connect all stages: Manager \u2192 Finance \u2192 End.",
    target: ".t2t-palette",
    action: "all_connected",
    successMsg: "Your workflow has a complete flow! Every stage is connected. No dead ends.",
    nextLabel: "Assign the right people to each step \u2192",
  },
  {
    id: "T7", step: 7,
    title: "Assign Roles",
    instruction: "Double-click on a stage to open its settings. In the Assigned Roles section, click a role to assign it to this stage. Do this for all stages.",
    target: ".t2t-snode",
    action: "role_assigned",
    successMsg: "Roles control who can approve requests at each step. You can assign roles to the other stages too.",
    nextLabel: "Now add a condition \u2192",
  },
  {
    id: "T8", step: 8,
    title: "Add a Condition",
    instruction: "Click the arrow between two stages to open its properties. Click \u201cAdd Conditions\u201d to create a routing rule \u2014 for example: if Amount > 10,000, route to Finance Director.",
    target: ".t2t-canvas-svg",
    action: "condition_saved",
    successMsg: "This rule says: amounts over the threshold need an extra level of approval.",
    nextLabel: "Last step \u2014 publish! \u2192",
  },
  {
    id: "T9", step: 9,
    title: "Publish the Workflow",
    instruction: "Your workflow is complete! Click Publish in the toolbar to make it live. Don\u2019t worry \u2014 you can always edit it later and the history is saved.",
    target: ".t2t-btn-primary", // Publish button
    action: "published",
    successMsg: "Tutorial complete! \ud83c\udf89 This workflow is now live.",
    nextLabel: null, // last step
  },
];

const T2TTutorial = ({ active, onClose, onComplete }) => {
  const toast = useToast();
  const { advanceTutorial, completeTutorial, state: guidanceState } = useGuidance();

  const [currentStep, setCurrentStep] = useState(1);
  const [actionDone, setActionDone] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const observerRef = useRef(null);

  // Resume if tutorial was in progress
  useEffect(() => {
    if (active && guidanceState.tutorial_current_step > 1) {
      setCurrentStep(guidanceState.tutorial_current_step);
    }
  }, [active, guidanceState.tutorial_current_step]);

  // Position spotlight on target element
  const updateSpotlight = useCallback(() => {
    const stepDef = TUTORIAL_STEPS[currentStep - 1];
    if (!stepDef?.target) { setSpotlightRect(null); return; }
    const el = document.querySelector(stepDef.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setSpotlightRect({ top: rect.top - 8, left: rect.left - 8, width: rect.width + 16, height: rect.height + 16 });
    } else {
      setSpotlightRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    if (!active) return;
    updateSpotlight();
    const interval = setInterval(updateSpotlight, 1000); // re-check position
    return () => clearInterval(interval);
  }, [active, currentStep, updateSpotlight]);

  // Listen for canvas events to detect user actions
  useEffect(() => {
    if (!active) return;
    const stepDef = TUTORIAL_STEPS[currentStep - 1];
    if (!stepDef?.action) return;

    const handleEvent = (e) => {
      const eventType = e.detail?.type || e.type;
      if (stepDef.action === "stage_added" && (eventType === "t2t-stage-added" || eventType === "drop")) {
        setActionDone(true); setShowSuccess(true);
      }
      if (stepDef.action === "stage_props_opened" && eventType === "t2t-props-opened") {
        setActionDone(true); setShowSuccess(true);
      }
      if (stepDef.action === "transition_drawn" && eventType === "t2t-transition-drawn") {
        setActionDone(true); setShowSuccess(true);
      }
      if (stepDef.action === "all_connected" && eventType === "t2t-transition-drawn") {
        setActionDone(true); setShowSuccess(true);
      }
      if (stepDef.action === "role_assigned" && eventType === "t2t-role-assigned") {
        setActionDone(true); setShowSuccess(true);
      }
      if (stepDef.action === "condition_saved" && eventType === "t2t-condition-saved") {
        setActionDone(true); setShowSuccess(true);
      }
      if (stepDef.action === "published" && eventType === "t2t-published") {
        handleComplete();
      }
    };

    window.addEventListener("t2t-stage-added", handleEvent);
    window.addEventListener("t2t-props-opened", handleEvent);
    window.addEventListener("t2t-transition-drawn", handleEvent);
    window.addEventListener("t2t-role-assigned", handleEvent);
    window.addEventListener("t2t-condition-saved", handleEvent);
    window.addEventListener("t2t-published", handleEvent);

    return () => {
      window.removeEventListener("t2t-stage-added", handleEvent);
      window.removeEventListener("t2t-props-opened", handleEvent);
      window.removeEventListener("t2t-transition-drawn", handleEvent);
      window.removeEventListener("t2t-role-assigned", handleEvent);
      window.removeEventListener("t2t-condition-saved", handleEvent);
      window.removeEventListener("t2t-published", handleEvent);
    };
  }, [active, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const goNext = async () => {
    if (currentStep >= TUTORIAL_STEPS.length) {
      handleComplete();
      return;
    }
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    setActionDone(false);
    setShowSuccess(false);
    advanceTutorial(nextStep);
  };

  const handleComplete = async () => {
    await completeTutorial();
    toast("Tutorial complete! \ud83c\udf89 You now know how to build workflows.", "success");
    if (onComplete) onComplete();
  };

  const handleExit = async () => {
    await postData("t2t/guidance/tutorial/step", { step: currentStep });
    onClose();
  };

  if (!active) return null;

  const stepDef = TUTORIAL_STEPS[currentStep - 1];
  const canProceed = !stepDef.action || actionDone; // T1 has no action, so always proceed
  const isLastStep = currentStep === TUTORIAL_STEPS.length;

  return (
    <>
      {/* Spotlight overlay */}
      {spotlightRect && (
        <div className="t2t-tutorial-spotlight" style={{
          boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.45)`,
          position: "fixed", zIndex: 900, borderRadius: 8, pointerEvents: "none",
          top: spotlightRect.top, left: spotlightRect.left,
          width: spotlightRect.width, height: spotlightRect.height,
          border: "2px solid var(--t2t-primary)",
          animation: "t2tSpotlightPulse 1.5s ease infinite",
        }} />
      )}

      {/* Tutorial Sidebar */}
      <div className="t2t-tutorial-sidebar">
        {/* Header */}
        <div className="t2t-tutorial-header">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>🎓</span>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Tutorial</span>
          </div>
          <button className="t2t-close-btn" onClick={handleExit} title="Exit tutorial" style={{ fontSize: 16 }}>×</button>
        </div>

        {/* Progress */}
        <div style={{ padding: "8px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 4 }}>
            <span>Step {currentStep} of {TUTORIAL_STEPS.length}</span>
            <span>{Math.round((currentStep / TUTORIAL_STEPS.length) * 100)}%</span>
          </div>
          <div style={{ height: 4, background: "#E2E8F0", borderRadius: 4 }}>
            <div style={{ height: "100%", width: `${(currentStep / TUTORIAL_STEPS.length) * 100}%`, background: "var(--t2t-primary)", borderRadius: 4, transition: "width 0.3s" }} />
          </div>
        </div>

        {/* Step content */}
        <div style={{ padding: "12px 16px", flex: 1, overflowY: "auto" }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{stepDef.title}</div>
          <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", lineHeight: 1.7, margin: 0 }}>{stepDef.instruction}</p>

          {/* Success message */}
          {showSuccess && stepDef.successMsg && (
            <div style={{ marginTop: 14, padding: "10px 12px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, fontSize: 12, color: "#166534", lineHeight: 1.6 }}>
              ✅ {stepDef.successMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--t2t-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {currentStep > 1 ? (
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => { setCurrentStep(currentStep - 1); setActionDone(false); setShowSuccess(false); }}>← Back</button>
          ) : <div />}

          {isLastStep ? (
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={handleComplete} disabled={!canProceed}>
              🎉 Finish Tutorial
            </button>
          ) : (
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={goNext} disabled={!canProceed}>
              {canProceed ? (stepDef.nextLabel || "Next →") : "Complete the action above..."}
            </button>
          )}
        </div>

        {/* Step indicators */}
        <div style={{ padding: "8px 16px 12px", display: "flex", gap: 4, justifyContent: "center" }}>
          {TUTORIAL_STEPS.map((_, i) => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: "50%",
              background: i + 1 < currentStep ? "var(--t2t-primary)" : i + 1 === currentStep ? "#1B3A6B" : "#E2E8F0",
              transition: "0.2s",
            }} />
          ))}
        </div>
      </div>
    </>
  );
};

T2TTutorial.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
};

export default T2TTutorial;

// ---- Helper: dispatch tutorial events from T2TBuilder ----
// Call these from the builder when user actions happen:
export const emitTutorialEvent = (type, detail = {}) => {
  window.dispatchEvent(new CustomEvent(type, { detail }));
};
